import { useState } from 'react';
import { Button, Card, Input, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCustomersList, type Customer } from '../api/customers';
import { CustomerFormModal } from '../components/customer-form-modal';

export function CustomersListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading } = useCustomersList({ search: search || undefined });

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>{t('customers.title')}</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          {t('customers.new')}
        </Button>
      </div>

      <Card bodyStyle={{ padding: 16 }}>
        <Input
          placeholder={t('customers.searchPlaceholder')}
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 360 }}
        />
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table<Customer>
          rowKey="id"
          loading={isLoading}
          dataSource={data?.data ?? []}
          pagination={false}
          onRow={(record) => ({ onClick: () => navigate(`/customers/${record.id}`), style: { cursor: 'pointer' } })}
          columns={[
            {
              title: t('customers.cols.name'),
              render: (_, c) => (
                <Space direction="vertical" size={0}>
                  <Space size={6}>
                    <Typography.Text strong>{c.lastName} {c.firstName}</Typography.Text>
                    {c.status === 'delinquent' && (
                      <Tag color="red" style={{ marginInlineEnd: 0 }}>{t('customers.status.delinquent')}</Tag>
                    )}
                  </Space>
                  {c.middleName && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{c.middleName}</Typography.Text>}
                </Space>
              ),
            },
            { title: t('customers.cols.phone'), dataIndex: 'phone', width: 180 },
            { title: t('customers.cols.email'), dataIndex: 'email', width: 220, ellipsis: true, render: (v: string | null) => v ?? '—' },
            {
              title: t('customers.cols.agent'),
              width: 180,
              render: (_, c) => c.assignedAgent?.fullName ?? '—',
            },
            {
              title: t('customers.cols.source'),
              dataIndex: 'source',
              width: 120,
              render: (s: string) => t(`customers.source.${s}`),
            },
          ]}
        />
      </Card>

      <CustomerFormModal
        mode="create"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(customer) => navigate(`/customers/${customer.id}`)}
      />
    </Space>
  );
}
